import { ProcedureRow } from "types/CreateOrder";

import {
  buildSelectedProceduresObject,
  buildSelectedProceduresToTable,
} from "./builders";
import { getCptQuantities, replicateByCptQuantity } from "./helpers";

export type PayerChangeKind = "hydration" | "unchanged" | "changed";

/**
 * Tells the payer that just hydrated on a redo/draft/copy (which must not rebuild the table
 * — `initializeDefault` is still seeding it) from the user picking a different payer (which
 * must, or the rows keep the previous payer's providerProcedureIds). CLE-2538 follow-up.
 */
export const classifyPayerChange = ({
  previousPayer,
  nextPayer,
}: {
  previousPayer: string | null | undefined;
  nextPayer: string | null | undefined;
}): PayerChangeKind => {
  // Recording an empty payer would make the real value arriving later look like a change.
  if (!nextPayer) return "unchanged";
  if (!previousPayer) return "hydration";
  if (previousPayer === nextPayer) return "unchanged";
  return "changed";
};

/**
 * CLE-2690: drop the CPT chips for codes the new payer does not carry, alongside their rows.
 * A chip left behind resurrects the line — the quantity control and the implant suggestions
 * read `autoCompleteValue`, not the rows.
 *
 * Filters per *unit*, not per code: `autoCompleteValue` holds one entry per unit and the UI
 * re-derives quantity by counting them, so the surviving codes must keep every duplicate.
 */
export const dropUnavailableEntries = ({
  autoCompleteValue,
  unavailableCptCodes,
}: {
  autoCompleteValue: string[];
  unavailableCptCodes: string[];
}): string[] => {
  const unavailable = new Set(unavailableCptCodes);
  if (!unavailable.size) return autoCompleteValue;

  return autoCompleteValue.filter((value) => {
    const [{ code }] = buildSelectedProceduresObject([value]);
    return !unavailable.has(code);
  });
};

/**
 * The warning under the CPT picker. A payer change rebuilds the table, so the codes really
 * are gone and the message has to say so; the visit-date refetch on redo/draft/copy leaves
 * the table alone, so there the same codes are only a heads-up.
 */
export const buildUnavailableCptMessage = ({
  unavailableCptCodes,
  removedFromTable,
}: {
  unavailableCptCodes: string[];
  removedFromTable: boolean;
}): string => {
  if (!unavailableCptCodes.length) return "";

  const codes = unavailableCptCodes.join(", ");
  return removedFromTable
    ? `Removed from this estimate — not available for this payer: ${codes}`
    : `CPT Codes Not Found for this payer - ${codes}`;
};

/**
 * `autoCompleteValue` holds one entry per *unit* — quantity is never persisted, the UI
 * re-derives it by counting duplicates. Without collapsing, the search fires one request per
 * unit and the rebuild replicates by quantity a second time, so a quantity-N line came back
 * as N * N rows. CLE-2538 fixed the same fan-out in the page initializers but not here.
 */
export const distinctByCptCode = (autoCompleteValue: string[]): string[] => {
  const seen = new Set<string>();

  return autoCompleteValue.filter((value) => {
    const [{ code }] = buildSelectedProceduresObject([value]);
    if (seen.has(code)) return false;
    seen.add(code);
    return true;
  });
};

/**
 * Fetch this account's provider procedures for a payer + visit date and rebuild the
 * procedure table from them. Pure in / out apart from `search`; the caller decides whether
 * to apply `nextRows` / `nextAutoCompleteValue` (a payer change must, a visit-date change on
 * redo/draft/copy must not — amounts may have been hand-edited on the original order).
 *
 * Codes the new payer does not carry produce no rebuilt row, and CLE-2690 is the rule that
 * none of them may survive by another route. A row kept from the previous payer keeps that
 * payer's `amount` and `providerProcedureId`, and both reach the estimate: the payload built
 * in GFEServiceDetails carries every row, and `pricing.service` resolves
 * `providerProcedureId` with no payer check.
 */
export const fetchPayerProcedures = async ({
  accountId,
  payerName,
  dateOfService,
  autoCompleteValue,
  getPreviousRows,
  search,
}: {
  accountId: string;
  payerName: string;
  dateOfService?: string;
  autoCompleteValue: string[];
  /** Read after the await: the table may have been seeded while the request was in flight. */
  getPreviousRows: () => ProcedureRow[];
  search: (args: {
    accountId: string;
    cptCode: string;
    payerName: string;
    dateOfService?: string;
  }) => Promise<any>;
}): Promise<{
  unavailableCptCodes: string[];
  nextRows: ProcedureRow[];
  nextAutoCompleteValue: string[];
}> => {
  const distinctValue = distinctByCptCode(autoCompleteValue);
  const cptCodesToFetch = buildSelectedProceduresObject(distinctValue).map(
    (item) => item.code
  );

  const responses = await Promise.all(
    cptCodesToFetch.map(async (cptCode) => {
      const response = await search({
        accountId,
        cptCode,
        payerName,
        ...(dateOfService ? { dateOfService } : {}),
      });
      return response?.data?.data?.data || [];
    })
  );

  const providerProcedures = responses.flat();
  const procedures = providerProcedures.map(
    (providerProcedure: any) => providerProcedure.procedure || []
  );

  // Availability is decided on the CPT code alone — the same key the rebuild matches on
  // (`getProcedureId`). Comparing the whole "code - description" label instead would report
  // a code the payer *does* carry as not found whenever the wording differs, and since
  // CLE-2690 that no longer just warns, it deletes the line.
  const availableCptCodes = new Set<string>(
    providerProcedures
      .map((providerProcedure: any) => providerProcedure?.procedure?.cptCode)
      .filter(Boolean)
  );

  const availableValue = distinctValue.filter((value) =>
    availableCptCodes.has(buildSelectedProceduresObject([value])[0].code)
  );
  const unavailableCptCodes = cptCodesToFetch.filter(
    (cptCode) => !availableCptCodes.has(cptCode)
  );

  const previousRows = getPreviousRows();
  const nextRows = buildSelectedProceduresToTable({
    selectedProceduresObject: replicateByCptQuantity(
      buildSelectedProceduresObject(availableValue),
      getCptQuantities(previousRows)
    ),
    procedures,
    providerProcedures,
    rows: [],
    row: undefined,
  });

  return {
    unavailableCptCodes,
    nextRows,
    nextAutoCompleteValue: dropUnavailableEntries({
      autoCompleteValue,
      unavailableCptCodes,
    }),
  };
};
